import { z } from 'zod';

export const ESTAGIOS = [
  'sinal_detectado',
  'triagem',
  'qualificacao',
  'falso_positivo_servico',
  'dossie',
  'abordagem',
  'proposta',
  'congelado',
  'fechado_ganho',
  'fechado_perdido',
  'recusado',
  'descartado',
] as const;
export type Estagio = (typeof ESTAGIOS)[number];

export const FRENTES = ['fundiario', 'ambiental', 'credito', 'juridico'] as const;
export type Frente = (typeof FRENTES)[number];

export const STATUS_SINAL = ['confirmado', 'falso_positivo', 'duplicado', 'ruido'] as const;
export type StatusSinal = (typeof STATUS_SINAL)[number];

export const TEMPERATURAS = ['quente', 'morna', 'fria'] as const;
export type Temperatura = (typeof TEMPERATURAS)[number];

export const cardSchema = z
  .object({
    id: z.string(),
    cliente_nome: z.string().nullish(),
    cliente_cpf: z.string(),
    municipio: z.string().nullish(),
    imovel_id: z.coerce.number().nullish(),
    imovel_denominacao: z.string().nullish(),
    imovel_matricula: z.string().nullish(),
    verificacao: z.string().nullish(),
    servico: z.string(),
    frente: z.enum(FRENTES),
    estagio: z.enum(ESTAGIOS),
    status_sinal: z.enum(STATUS_SINAL).nullish(),
    temperatura: z.enum(TEMPERATURAS).nullish(),
    potencial_min: z.coerce.number().nullish(),
    potencial_max: z.coerce.number().nullish(),
    proposta_valor: z.coerce.number().nullish(),
    valor_fechado: z.coerce.number().nullish(),
    prazo_abordagem: z.string().nullish(),
    analista_id: z.coerce.number().nullish(),
    analista_nome: z.string().nullish(),
    comercial_id: z.coerce.number().nullish(),
    comercial_nome: z.string().nullish(),
    dossie_status: z.string().nullish(),
    dossie_url: z.string().nullish(),
    fonte: z.string().nullish(),
    dias_na_coluna: z.coerce.number().default(0),
    detalhe: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .passthrough();
export type Card = z.infer<typeof cardSchema>;

export const colunaSchema = z.object({
  id: z.enum(ESTAGIOS),
  nome: z.string(),
  cor: z.string().nullish(),
  sla_dias: z.coerce.number().nullish(),
  processos: z.array(cardSchema).default([]),
});
export type Coluna = z.infer<typeof colunaSchema>;

export const boardSchema = z.object({
  colunas: z.array(colunaSchema).default([]),
  total: z.coerce.number().default(0),
});
export type Board = z.infer<typeof boardSchema>;

export const anexoSchema = z.object({
  nome: z.string(),
  filename: z.string(),
  mime: z.string(),
  tamanho: z.coerce.number(),
  url: z.string(),
  anexado_por_id: z.coerce.number().nullish(),
  anexado_por_nome: z.string().nullish(),
  anexado_em: z.string().nullish(),
});
export type Anexo = z.infer<typeof anexoSchema>;

export const eventoSchema = z.object({
  id: z.coerce.number(),
  tipo: z.string(),
  estagio_de: z.string().nullish(),
  estagio_para: z.string().nullish(),
  ator_id: z.coerce.number().nullish(),
  ator_nome: z.string().nullish(),
  payload: z.unknown().nullish(),
  anexos: z.array(anexoSchema).nullish().transform((v) => v ?? []),
  editado_em: z.string().nullish(),
  editado_por_id: z.coerce.number().nullish(),
  editado_por_nome: z.string().nullish(),
  created_at: z.string(),
});
export type Evento = z.infer<typeof eventoSchema>;

export const origemSchema = z
  .object({
    id: z.coerce.number().nullish(),
    verificacao: z.string().nullish(),
    status: z.string().nullish(),
    detalhe: z.string().nullish(),
    oportunidade: z.string().nullish(),
    fonte_url: z.string().nullish(),
    verificado_em: z.string().nullish(),
    dados_raw: z.unknown().nullish(),
  })
  .passthrough();
export type Origem = z.infer<typeof origemSchema>;

// Perfil comportamental do cliente — 1:1 com cliente, atualizado na qualificação.
export const perfilSchema = z
  .object({
    cliente_cpf: z.string(),
    decisor: z.string().nullish(),
    velocidade_decisao: z.string().nullish(),
    sensibilidade_preco: z.string().nullish(),
    reacao_regulatorio: z.string().nullish(),
    status_relacionamento: z.string().nullish(),
    ja_recusou_servicos: z.boolean().nullish(),
    detalhe_recusas: z.string().nullish(),
    conhecimento_tema: z.string().nullish(),
    canal_preferido: z.string().nullish(),
    maturidade_operacao: z.string().nullish(),
    notas_perfil: z.string().nullish(),
    atualizado_em: z.string().nullish(),
    atualizado_por: z.coerce.number().nullish(),
  })
  .passthrough();
export type Perfil = z.infer<typeof perfilSchema>;

export const detailSchema = z.object({
  oportunidade: z.record(z.unknown()),
  origem: origemSchema.nullish(),
  imovel: z.record(z.unknown()).nullish(),
  perfil: perfilSchema.nullish(),
  eventos: z.array(eventoSchema).default([]),
});
export type Detail = z.infer<typeof detailSchema>;

// Catálogos de Abordagem — usados no form da fase
export const ABORDAGEM_CANAIS = [
  { v: 'whatsapp', label: 'WhatsApp' },
  { v: 'telefone', label: 'Telefone' },
  { v: 'email', label: 'E-mail' },
  { v: 'presencial', label: 'Presencial' },
  { v: 'video', label: 'Vídeo chamada' },
  { v: 'outro', label: 'Outro' },
] as const;

export const ABORDAGEM_RESULTADOS = [
  { v: 'atendeu_interessado', label: 'Atendeu, interessado', tone: 'ok' as const },
  { v: 'atendeu_pediu_retorno', label: 'Atendeu, pediu retorno', tone: 'info' as const },
  { v: 'atendeu_sem_interesse', label: 'Atendeu, sem interesse', tone: 'danger' as const },
  { v: 'mensagem_lida_sem_resposta', label: 'Mensagem lida, sem resposta', tone: 'warn' as const },
  { v: 'mensagem_nao_lida', label: 'Mensagem não lida', tone: 'neutral' as const },
  { v: 'nao_atendeu', label: 'Não atendeu', tone: 'neutral' as const },
] as const;

export const ABORDAGEM_ACOES = [
  { v: 'agendar_retorno', label: 'Agendar retorno' },
  { v: 'enviar_material', label: 'Enviar material / dossiê' },
  { v: 'enviar_proposta', label: 'Enviar proposta (avança fase)' },
  { v: 'aguardar_cliente', label: 'Aguardar cliente responder' },
  { v: 'abandonar', label: 'Abandonar (perde a oportunidade)' },
] as const;

// Catálogos da fase Proposta
export const PROPOSTA_CANAIS = [
  { v: 'whatsapp', label: 'WhatsApp' },
  { v: 'email', label: 'E-mail' },
  { v: 'presencial', label: 'Presencial' },
  { v: 'video', label: 'Vídeo chamada' },
  { v: 'outro', label: 'Outro' },
] as const;

export const PROPOSTA_PAGAMENTOS = [
  { v: 'a_vista', label: 'À vista' },
  { v: 'parcelado', label: 'Parcelado' },
  { v: 'misto', label: 'Entrada + parcelas' },
  { v: 'a_combinar', label: 'A combinar' },
] as const;

export const PROPOSTA_PROBABILIDADES = [
  { v: 'alta', label: 'Alta', tone: 'ok' as const },
  { v: 'media', label: 'Média', tone: 'warn' as const },
  { v: 'baixa', label: 'Baixa', tone: 'danger' as const },
] as const;

// Catálogo dos enums — usado pelo form
export const PERFIL_OPCOES = {
  decisor: [
    { v: 'proprietario', label: 'Proprietário' },
    { v: 'conjuge', label: 'Cônjuge' },
    { v: 'filho_sucessor', label: 'Filho / sucessor' },
    { v: 'contador', label: 'Contador' },
    { v: 'gerente', label: 'Gerente da fazenda' },
    { v: 'advogado', label: 'Advogado' },
    { v: 'outro', label: 'Outro' },
  ],
  velocidade_decisao: [
    { v: 'rapido', label: 'Rápido (1-3 dias)' },
    { v: 'pondera', label: 'Pondera (1-2 semanas)' },
    { v: 'longo', label: 'Longo (mais de 1 mês)' },
    { v: 'imprevisivel', label: 'Imprevisível' },
  ],
  sensibilidade_preco: [
    { v: 'baixa', label: 'Baixa — decide pelo serviço' },
    { v: 'media', label: 'Média' },
    { v: 'alta', label: 'Alta — sempre negocia' },
  ],
  reacao_regulatorio: [
    { v: 'proativo', label: 'Proativo / preventivo' },
    { v: 'reativo', label: 'Reativo (após autuação)' },
    { v: 'resistente', label: 'Resistente' },
  ],
  status_relacionamento: [
    { v: 'novo', label: 'Novo (primeiro contato)' },
    { v: 'em_curso', label: 'Primeiro contato em curso' },
    { v: 'ativo', label: 'Cliente ativo' },
    { v: 'recorrente', label: 'Recorrente fidelizado' },
    { v: 'ex_cliente', label: 'Ex-cliente' },
  ],
  conhecimento_tema: [
    { v: 'baixo', label: 'Baixo (precisa explicação básica)' },
    { v: 'medio', label: 'Médio' },
    { v: 'alto', label: 'Alto (já entende a norma)' },
  ],
  canal_preferido: [
    { v: 'whatsapp', label: 'WhatsApp' },
    { v: 'telefone', label: 'Telefone' },
    { v: 'email', label: 'E-mail' },
    { v: 'presencial', label: 'Presencial' },
    { v: 'misto', label: 'Misto' },
  ],
  maturidade_operacao: [
    { v: 'fundador', label: 'Fundador no comando' },
    { v: 'sucessor_recente', label: 'Sucessor recente' },
    { v: 'espolio', label: 'Espólio em andamento' },
    { v: 'estabelecida', label: 'Estabelecida' },
  ],
} as const;

export const catalogoItemSchema = z.object({
  verificacao: z.string().nullable(),
  frente: z.enum(FRENTES),
  servico: z.string(),
  servico_falso_pos: z.string().nullish(),
  ordem: z.coerce.number().default(100),
});
export const catalogoSchema = z.object({
  catalogo: z.array(catalogoItemSchema).default([]),
});

export const metricasSchema = z.object({
  total_por_estagio: z.array(z.object({ estagio: z.string(), qtd: z.coerce.number() })).default([]),
  ativos_por_frente: z.array(z.object({ frente: z.string().nullable(), qtd: z.coerce.number() })).default([]),
  falso_positivo_por_verificacao: z.array(
    z.object({
      verificacao: z.string().nullable(),
      falso_pos: z.coerce.number(),
      confirmados: z.coerce.number(),
      total: z.coerce.number(),
    }),
  ).default([]),
  ganhos: z.object({ qtd: z.coerce.number(), valor: z.coerce.number() }).nullish(),
  tempo_medio_por_coluna: z.array(
    z.object({ estagio: z.string(), dias_medio: z.coerce.number().nullable() }),
  ).default([]),
  area_ativa_ha: z.coerce.number().default(0),
  area_carteira_ha: z.coerce.number().default(0),
  pipeline_ponderado: z.coerce.number().default(0),
  pipeline_qtd_com_valor: z.coerce.number().default(0),
  pipeline_valor_bruto: z.coerce.number().default(0),
});

export const ESTAGIO_LABEL: Record<Estagio, string> = {
  sinal_detectado: 'Sinal detectado',
  triagem: 'Triagem',
  qualificacao: 'Qualificação',
  falso_positivo_servico: 'Falso positivo — laudo',
  dossie: 'Dossiê',
  abordagem: 'Abordagem',
  proposta: 'Proposta',
  congelado: 'Congelado',
  fechado_ganho: 'Aprovado',
  fechado_perdido: 'Negado',
  recusado: 'Recusado',
  descartado: 'Descartado',
};

// Catálogo de motivos de recusa — usado pelo form de fechamento
export const RECUSA_MOTIVOS = [
  { v: 'preco', label: 'Preço acima do esperado' },
  { v: 'prazo', label: 'Prazo de execução longo demais' },
  { v: 'concorrente', label: 'Fechou com concorrente' },
  { v: 'fez_internamente', label: 'Vai fazer internamente / com pessoa de confiança' },
  { v: 'sem_urgencia', label: 'Decidiu deixar pra depois' },
  { v: 'sem_orcamento', label: 'Sem orçamento agora' },
  { v: 'nao_e_decisor', label: 'Não é o decisor — não conseguiu aprovar' },
  { v: 'mudou_de_ideia', label: 'Cliente mudou de ideia / não precisa mais' },
  { v: 'sem_resposta', label: 'Cliente parou de responder' },
  { v: 'duplicado', label: 'Oportunidade duplicada' },
  { v: 'outro', label: 'Outro (descrever em observação)' },
] as const;

export const FRENTE_LABEL: Record<Frente, string> = {
  fundiario: 'Fundiário',
  ambiental: 'Ambiental',
  credito: 'Crédito',
  juridico: 'Jurídico',
};

export const TEMPERATURA_LABEL: Record<Temperatura, string> = {
  quente: 'Quente',
  morna: 'Morna',
  fria: 'Fria',
};
